import type { Styling } from 'src/context/styling';
import type { BuildType } from 'src/enums/build/build-type';
import type { Language } from 'src/enums/language';
import { PromoRibbonStyle } from 'src/enums/promo-ribbon-style';
import type { LocalStorageKey } from 'src/helpers/local-storage-helper';
import { getLocalStorageItem, setLocalStorageItem } from 'src/helpers/local-storage-helper';
import { isClubLinkCurrentPage, isGlobalLinkCurrentPage } from 'src/helpers/navigation-helper';

export const isPromoEnabledOnPage = (
  promoRibbon: Queries.HeaderPromoRibbon | Queries.GlobalHeaderRecentlyViewedClubRibbon | undefined,
  basePath: string,
  language: Language,
  buildType: BuildType,
  clubId?: string
) => {
  if (!promoRibbon?.enabled || !promoRibbon?.selectedPages) {
    return false;
  }

  if (promoRibbon.selectedPages.allPages) {
    return true;
  }

  const pagesEnabledOn = [
    ...(promoRibbon.selectedPages.homePage ? ['/'] : []),
    ...(promoRibbon.selectedPages.levelOnePages ?? []),
    ...(promoRibbon.selectedPages.levelTwoPages ?? []),
    ...(promoRibbon.selectedPages.levelThreePages ?? []),
  ];

  if (clubId) {
    return pagesEnabledOn.some((link) => isClubLinkCurrentPage(link, language, clubId, basePath));
  }

  return pagesEnabledOn.some((link) =>
    isGlobalLinkCurrentPage(link, language, buildType, basePath)
  );
};

export const backgroundAndTextColorForRibbon = (styling: Styling, color?: string | null) => {
  switch (color) {
    case PromoRibbonStyle.PLUM:
      return { backgroundColor: styling.colors.camelot, color: styling.colors.lightBackground };
    case PromoRibbonStyle.GREEN:
      return { backgroundColor: styling.colors.darkGreen, color: styling.colors.lightBackground };
    case PromoRibbonStyle.BLACK:
      return { backgroundColor: '#000', color: styling.colors.lightBackground };
    case PromoRibbonStyle.RED:
      return {
        backgroundColor: styling.colors.errorBoundary,
        color: styling.colors.lightBackground,
      };
    case PromoRibbonStyle.LIGHT_GREEN:
      return { backgroundColor: styling.colors.lightGreen, color: styling.colors.capeCod };
    case PromoRibbonStyle.ORANGE:
      return {
        backgroundColor: styling.colors.meridianOrange,
        color: styling.colors.lightBackground,
      };
    case PromoRibbonStyle.NAVY_BLUE:
      return {
        backgroundColor: styling.colors.meridianBlue,
        color: styling.colors.lightBackground,
      };
    case PromoRibbonStyle.GOLD_WITH_DARK_TEXT:
      return {
        backgroundColor: styling.colors.harbourGold,
        color: styling.colors.harbourSlateGrey,
      };
    case PromoRibbonStyle.GOLD_WITH_WHITE_TEXT:
      return {
        backgroundColor: styling.colors.harbourGold,
        color: 'white',
      };
    case PromoRibbonStyle.HARBOUR_GREY_WITH_GOLD_TEXT:
      return {
        backgroundColor: styling.colors.harbourSlateGrey,
        color: styling.colors.harbourGold,
      };
    case PromoRibbonStyle.HARBOUR_GREY_WITH_WHITE_TEXT:
      return {
        backgroundColor: styling.colors.harbourSlateGrey,
        color: 'white',
      };
    default:
      return { backgroundColor: styling.colors.camelot, color: styling.colors.lightBackground };
  }
};

export const setRecentlyViewedClubIdForKey = (key: LocalStorageKey, clubId?: string) => {
  if (!clubId) return;
  const recentlyViewedClubId = getLocalStorageItem(key);

  if (!recentlyViewedClubId || recentlyViewedClubId !== clubId) {
    setLocalStorageItem(key, clubId);
  }
};
